import React from "react";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { BRAND } from "../../data/content";
import { useOrder } from "../../context/OrderContext";
import { Reveal } from "./Reveal";
import { BrandName } from "./BrandName";

export const Location = () => {
  const { openOrder } = useOrder();
  return (
    <section id="visit" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 scroll-mt-20">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 lg:items-stretch">
        <div>
          <Reveal><p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Pickup & Delivery</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-3 font-display text-4xl sm:text-6xl font-semibold tracking-tight text-cream">
              Find <BrandName className="text-4xl sm:text-6xl" />
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-4 max-w-md text-sm sm:text-base text-cream/60 leading-relaxed">
              We run as a dedicated ghost kitchen — grab it fresh for pickup or get it delivered to your door.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            <Reveal delay={0.08}>
              <div className="flex gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold/15 border border-gold/30 text-gold"><MapPin size={18} /></span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-cream/45">Address</p>
                  <p className="mt-1 font-semibold text-cream">{BRAND.addressLine1}</p>
                  <p className="text-cream/70">{BRAND.addressLine2}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.13}>
              <div className="flex gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold/15 border border-gold/30 text-gold"><Clock size={18} /></span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-cream/45">Hours</p>
                  <p className="mt-1 font-semibold text-cream">Open 7 days a week</p>
                  <div className="mt-1 space-y-0.5">
                    {BRAND.hoursList.map(([days, time]) => (
                      <p key={days} className="flex justify-between gap-6 text-cream/70">
                        <span>{days}</span>
                        <span className="text-gold">{time}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="flex gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold/15 border border-gold/30 text-gold"><Phone size={18} /></span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-cream/45">Phone</p>
                  <a href={BRAND.phoneHref} className="mt-1 block font-semibold text-cream hover:text-gold transition-colors">{BRAND.phoneDisplay}</a>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.22}>
            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={openOrder} data-testid="location-order-now" className="inline-flex items-center rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5 gold-glow">
                Order Now
              </button>
              <a href={BRAND.mapsDirections} target="_blank" rel="noreferrer" data-testid="location-directions" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-cream hover:border-gold/40 hover:text-gold transition-colors">
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="min-h-[360px] lg:min-h-0">
          <div className="relative h-full min-h-[360px] overflow-hidden rounded-3xl border border-white/10">
            <iframe
              title="Smokey & Creamy location map"
              src={BRAND.mapsEmbed}
              className="h-full w-full map-dark"
              style={{ border: 0, minHeight: 360 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/10 rounded-3xl" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Location;
